import classnames from 'classnames';
import React from 'react';
import styles from './button.css';
import { SharedButtonProps } from './button';

export interface ButtonLinkProps extends SharedButtonProps {
  /** The content to display inside the button */
  children?: string | JSX.Element;
  /** URL to redirect the user when clicked */
  href?: string;
  /** Sets the target of the a tag */
  target?: '_blank' | '_self' | '_parent' | '_top';
  /** Provides visual elements for large buttons used on forms */
  /** HTML ID */
  id?: string;
  /** Callback when clicked */
  onClick?: (e: React.MouseEvent) => void;
  /** Used by the Ruby On Rails framework to submit the form/link/button as an ajax request and used as a hack to trigger the rails route in a modal. */
  'data-remote'?: string;
  /** Provides a data-testid */
  'data-testid'?: string;
}

enum ButtonType {
  Primary = 'primary',
  Secondary = 'secondary',
  Tertiary = 'tertiary',
  Remove = 'remove'
}

/**
 * A Button that behaves like a Link
 *
 * @param {ButtonLinkProps} props
 *
 * @returns JSX.ELEMENT
 */
const ButtonLink = ({
  children,
  className,
  href,
  onClick,
  'data-testid': dataTestId,
  name = 'button-link',
  id = 'button-link',
  'data-remote': dataRemote,
  large = false,
  disabled = false,
  primary = false,
  secondary = false,
  tertiary = false,
  remove = false,
  darkEnv = false,
  fullWidth = false,
  target = '_self'
}: ButtonLinkProps) => {
  const dataRemoteValue = dataRemote === 'true' ? { 'data-remote': true } : {};

  const styleType = (): ButtonType => {
    if (primary) {
      return ButtonType.Primary;
    }
    if (secondary) {
      return ButtonType.Secondary;
    }
    if (tertiary) {
      return ButtonType.Tertiary;
    }
    if (remove) {
      return ButtonType.Remove;
    }
    return ButtonType.Primary;
  };

  const composedClassName = classnames(
    styles.button,
    {
      [styles[styleType()]]: true,
      [styles.darkEnv]: darkEnv,
      [styles.disabled]: disabled,
      [styles.large]: large,
      [styles.fullWidth]: fullWidth
    },
    className
  );

  return (
    <a
      {...(href && !disabled && { href })}
      {...(name && { name })}
      {...(target && { target })}
      className={composedClassName}
      id={id}
      {...dataRemoteValue}
      data-testid={dataTestId}
      onClick={onClick}
    >
      {children}
    </a>
  );
};

export default ButtonLink;
